import S from 's-js';
import { SDataArray } from 's-array';
import { IAjax } from 'lib/Ajax';
import { Entrypoints } from 'core/app/entrypoints';
import { Image, ImageData } from 'data/models/Image';
import { DefaultImage } from 'data/models/DefaultImage';
import { UploadImageResponseData } from 'data/models/UploadFileResponse';
import { UploadFile, UploadFileParams } from 'portcall/uploadfile/model';
import { CropImage, CropSelection, CropImageParams } from 'portcall/cropimage/model';

export interface SelectProfileImageParams {
    image : Image;
    allowedImageUploadExtensions : () => string[];
    maximumImageUploadFileSize : () => number;
    minimumImageUploadImageSize : () => number;
    defaultImages : SDataArray<DefaultImage>;
    saveCroppedUploadUrl : string;
    saveImageSelection : () => void;
    onHide : () => void;
}

export function SelectProfileImage(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cropImageModel : (params : CropImageParams) => CropImage,
    uploadFileModel : (params : UploadFileParams<UploadImageResponseData>) => UploadFile,
    params : SelectProfileImageParams) {
    let cropImageParams = S.data<CropImageParams | null>(null),
        imageCropper = S(() => cropImageParams() && cropImageModel(cropImageParams()!)),
        fileUploader = uploadFileModel({
            onComplete: UploadComplete
        });

    return {
        FileUploader: fileUploader,
        Image: params.image,
        CropUploadedImage: CropUploadedImage,
        SelectUploadedImage: SelectUploadedImage,
        DefaultImages: params.defaultImages.map(DefaultImageViewModel),
        ImageCropper: imageCropper,
        Hide: params.onHide
    };

    function UploadComplete(data : UploadImageResponseData) {
        OpenCropper(data);
    }

    function CropUploadedImage() {
        S.sample(() => 
            OpenCropper({
                Url: (params.image.RawUrl() || '').split('?')[0],
                ImageWidth: params.image.RawWidth(),
                ImageHeight: params.image.RawHeight()
            }, new CropSelection(params.image.CropX1(), params.image.CropY1(), params.image.CropX2(), params.image.CropY2())
        ));
    }

    function OpenCropper(data : UploadImageResponseData, initialSelection : CropSelection | null = null) {
        cropImageParams(S.sample(() => ({
            imageUrl: data.Url || '',
            imageHeight: data.ImageHeight,
            imageWidth: data.ImageWidth,
            initialSelection: initialSelection,
            minSize: params.minimumImageUploadImageSize(),
            saveUrl: params.saveCroppedUploadUrl,
            onComplete: CropComplete,
            onCloseModal: () => cropImageParams(null)
        })));
    }

    function CropComplete(data : ImageData) {
        Image.update(params.image, data);
    }

    function SelectUploadedImage() {
        if (!S.sample(() => params.image.UploadProfileUrl())) throw new Error('Can\'t select the last uploaded image b/c no image has been uploaded');
        SelectImage(() => params.image.UploadProfileUrl()!, () => params.image.UploadThumbUrl()!);
    }

    function DefaultImageViewModel(defaultImage : DefaultImage) {
        return {
            Model: defaultImage,
            Select : Select
        };

        function Select() {
            SelectImage(() => defaultImage.ProfileUrl() || '', () => defaultImage.ThumbUrl() || '');
        }
    }

    function SelectImage(profileUrl : () => string, thumbUrl : () => string) {
        S.freeze(() => S.sample(() => {
            params.image.ProfileUrl(profileUrl());
            params.image.ThumbUrl(thumbUrl());
        }));
        params.saveImageSelection();         
    }
}
export type SelectProfileImage = ReturnType<typeof SelectProfileImage>;


// WEBPACK FOOTER //
// ./apps/portcall/selectprofileimage/model.ts